// Centralised bilingual strings (IT/ES) for cross-cutting UI: VideoPlayer
// security overlays, anti-piracy warnings, cookie banner and Terms-of-Service
// modals. Pages import the slice they need via `useI18n(lang).<slice>`.
//
// Design: keep this file the SINGLE source of truth for these messages so we
// never end up with one wording on the cookie banner and a different one on
// the privacy page. Adding a new key here forces both languages to be
// updated together (lint-friendly — missing keys throw at runtime).

const dict = {
  it: {
    video: {
      loading: "Caricamento video…",
      loading_hint: "Stiamo preparando il flusso più rapido possibile.",
      empty: "Nessun video disponibile per questa lezione.",
      empty_hint: "Il video sarà aggiunto dal team Academia.",
      label: "Video lezione",
      open_external: "Apri il video",
      lang_mismatch_prefix: "Questo video è disponibile in",
      lang_mismatch_suffix: "La tua lingua preferita è",
      protected: "Contenuto protetto · uso personale",
      blocked_title: "Account temporaneamente bloccato",
      blocked_body: "Sono stati rilevati ripetuti tentativi di violare i termini di utilizzo. Contatta il supporto per ripristinare l'accesso.",
    },
    security: {
      modal_eyebrow: "Avviso di sicurezza",
      modal_title_1: "Contenuto privato e protetto",
      modal_title_2: "Secondo avviso · attenzione",
      modal_title_3: "Account bloccato",
      modal_body: "Il contenuto di questa Academia è privato e protetto. Tentare di scaricare, copiare, registrare, estrarre o ridistribuire questo materiale viola i Termini di Utilizzo. Al terzo tentativo, l'account sarà bloccato definitivamente e perderai l'accesso al contenuto acquistato senza diritto al rimborso.",
      modal_body_2: "Questo è il tuo secondo avviso. Un ulteriore tentativo comporterà il blocco definitivo dell'account, senza diritto al rimborso del contenuto acquistato.",
      modal_body_3: "Il tuo account è stato bloccato per ripetute violazioni dei termini di utilizzo. Per richiedere una revisione, contatta il supporto.",
      modal_ack: "Ho capito",
      modal_blocked_cta: "Vai alla home",
      modal_appeal_btn: "Non ho commesso una violazione — Reclama",
      strike_count_label: "Avvisi registrati",
      appeal_title: "Richiesta di revisione",
      appeal_intro: "Se ritieni che questo avviso sia stato mostrato per errore, descrivi brevemente cosa è successo. Il nostro team valuterà la richiesta.",
      appeal_placeholder: "Spiega cosa è successo (obbligatorio)…",
      appeal_submit: "Invia richiesta",
      appeal_cancel: "Annulla",
      appeal_submitting: "Invio in corso…",
      appeal_success: "Richiesta inviata. Il tuo caso sarà revisionato. Finché la revisione è in sospeso, questo evento non verrà conteggiato come strike.",
      appeal_error_required: "La spiegazione è obbligatoria.",
      appeal_error_generic: "Impossibile inviare la richiesta. Riprova.",
      appeal_close: "Chiudi",
    },
    cookies: {
      title: "Utilizziamo i cookie",
      body: "Usiamo i cookie per garantire il funzionamento del sito, misurare l'audience e migliorare l'esperienza. Puoi accettare tutti i cookie, rifiutare quelli non essenziali o configurare le tue preferenze. I cookie strettamente necessari sono sempre attivi.",
      accept_all: "Accetta tutti",
      reject_all: "Rifiuta non essenziali",
      configure: "Configura preferenze",
      save: "Salva preferenze",
      policy_link: "Politica dei cookie",
      cat_necessary: "Necessari",
      cat_necessary_desc: "Indispensabili per il login, la sicurezza e la navigazione. Sempre attivi.",
      cat_analytics: "Analitici",
      cat_analytics_desc: "Ci aiutano a capire come usi il sito (statistiche aggregate, mai personali).",
      cat_marketing: "Marketing",
      cat_marketing_desc: "Personalizzazione delle offerte commerciali e remarketing.",
      cat_functional: "Funzionali",
      cat_functional_desc: "Memorizzano le preferenze (lingua, ultime lezioni viste) per migliorare l'esperienza.",
      always_on: "Sempre attivi",
    },
    terms: {
      modal_title: "Termini e Condizioni",
      checkbox: "Ho letto e accetto i Termini e Condizioni, la Politica sulla Privacy e le norme di utilizzo dell'Academia.",
      cta_open: "Leggi i Termini",
      block_until_accept: "Devi accettare i Termini per procedere all'acquisto.",
      sections: [
        { title: "Uso personale", body: "Il contenuto è privato e di uso personale. È vietato scaricare, copiare, registrare, condividere, rivendere o ridistribuire il materiale." },
        { title: "Antifrode", body: "Il tentativo di furto, download o distribuzione del contenuto può comportare il blocco definitivo dell'account, senza diritto al rimborso." },
        { title: "Account personale", body: "L'account è personale e non trasferibile. È vietato condividere password o credenziali di accesso." },
        { title: "Sospensione", body: "Médicos para Italia può sospendere account per uso fraudolento, condivisione di accesso o violazione dei presenti termini." },
        { title: "Rimborsi", body: "In caso di violazione grave delle presenti norme, l'utente perde l'accesso al contenuto acquistato e non avrà diritto al rimborso." },
        { title: "Modifiche", body: "I termini possono essere aggiornati. Le modifiche entrano in vigore al momento della pubblicazione sul sito." },
      ],
      close: "Chiudi",
    },
  },
  es: {
    video: {
      loading: "Cargando video…",
      loading_hint: "Estamos preparando el flujo más rápido posible.",
      empty: "No hay video disponible para esta lección.",
      empty_hint: "El video será agregado por el equipo Academia.",
      label: "Video de la lección",
      open_external: "Abrir el video",
      lang_mismatch_prefix: "Este video está disponible en",
      lang_mismatch_suffix: "Tu idioma preferido es",
      protected: "Contenido protegido · uso personal",
      blocked_title: "Cuenta temporalmente bloqueada",
      blocked_body: "Se detectaron repetidos intentos de violar los términos de uso. Contactá al soporte para restablecer el acceso.",
    },
    security: {
      modal_eyebrow: "Advertencia de seguridad",
      modal_title_1: "Contenido privado y protegido",
      modal_title_2: "Segunda advertencia · atención",
      modal_title_3: "Cuenta bloqueada",
      modal_body: "El contenido de esta Academia es privado y está protegido. Intentar descargar, copiar, grabar, extraer o redistribuir este material infringe los Términos y Condiciones de uso. Si vuelve a intentarlo, su cuenta podrá ser suspendida. Al tercer intento, la cuenta será bloqueada definitivamente y perderá el acceso al contenido adquirido sin derecho a reembolso.",
      modal_body_2: "Esta es tu segunda advertencia. Otro intento bloqueará tu cuenta de forma definitiva, sin derecho a reembolso del contenido adquirido.",
      modal_body_3: "Tu cuenta ha sido bloqueada por reiteradas violaciones a los términos de uso. Para solicitar una revisión, contactá al soporte.",
      modal_ack: "Entendido",
      modal_blocked_cta: "Ir al inicio",
      modal_appeal_btn: "No he cometido una violación — Reclama",
      strike_count_label: "Avisos registrados",
      appeal_title: "Solicitud de revisión",
      appeal_intro: "Si creés que este aviso fue mostrado por error, describí brevemente qué pasó. Nuestro equipo evaluará la solicitud.",
      appeal_placeholder: "Explicá qué pasó (obligatorio)…",
      appeal_submit: "Enviar solicitud",
      appeal_cancel: "Cancelar",
      appeal_submitting: "Enviando…",
      appeal_success: "Solicitud enviada. Tu caso será revisado. Mientras la revisión esté pendiente, este evento no se contará como strike.",
      appeal_error_required: "La explicación es obligatoria.",
      appeal_error_generic: "No se pudo enviar la solicitud. Intentá de nuevo.",
      appeal_close: "Cerrar",
    },
    cookies: {
      title: "Usamos cookies",
      body: "Utilizamos cookies para garantizar el funcionamiento del sitio, medir audiencia y mejorar la experiencia. Podés aceptar todas las cookies, rechazar las no esenciales o configurar tus preferencias. Las cookies estrictamente necesarias están siempre activas.",
      accept_all: "Aceptar todas",
      reject_all: "Rechazar no esenciales",
      configure: "Configurar preferencias",
      save: "Guardar preferencias",
      policy_link: "Política de cookies",
      cat_necessary: "Necesarias",
      cat_necessary_desc: "Indispensables para el login, la seguridad y la navegación. Siempre activas.",
      cat_analytics: "Analíticas",
      cat_analytics_desc: "Nos ayudan a entender cómo usás el sitio (estadísticas agregadas, nunca personales).",
      cat_marketing: "Marketing",
      cat_marketing_desc: "Personalización de ofertas comerciales y remarketing.",
      cat_functional: "Funcionales",
      cat_functional_desc: "Memorizan preferencias (idioma, últimas lecciones vistas) para mejorar la experiencia.",
      always_on: "Siempre activas",
    },
    terms: {
      modal_title: "Términos y Condiciones",
      checkbox: "He leído y acepto los Términos y Condiciones, la Política de Privacidad y las normas de uso de la Academia.",
      cta_open: "Leer los Términos",
      block_until_accept: "Debés aceptar los Términos para proceder con la compra.",
      sections: [
        { title: "Uso personal", body: "El contenido es privado y de uso personal. Está prohibido descargar, copiar, grabar, compartir, revender o redistribuir el material." },
        { title: "Antifraude", body: "El intento de robo, descarga o distribución del contenido puede causar el bloqueo definitivo de la cuenta, sin derecho a reembolso." },
        { title: "Cuenta personal", body: "La cuenta es personal e intransferible. Está prohibido compartir contraseña o acceso." },
        { title: "Suspensión", body: "Médicos para Italia puede suspender cuentas por uso fraudulento, compartir acceso o violar estos términos." },
        { title: "Reembolsos", body: "En caso de infracción grave de estas normas, el usuario pierde el acceso al contenido adquirido y no tendrá derecho a reembolso." },
        { title: "Modificaciones", body: "Los términos pueden actualizarse. Las modificaciones entran en vigor al momento de su publicación en el sitio." },
      ],
      close: "Cerrar",
    },
  },
};

export function useI18n(lang) {
  const safe = lang === "es" ? "es" : "it";
  return dict[safe];
}

export function getI18n(lang) {
  const safe = lang === "es" ? "es" : "it";
  return dict[safe];
}

export default dict;
